import { Component, inject } from '@angular/core';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-question-and-answer',
  standalone: true,
  imports: [
    CdkAccordionModule,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './question-and-answer.component.html',
  styleUrl: './question-and-answer.component.css'
})
export class QuestionAndAnswerComponent {
  private sanitizer = inject(DomSanitizer);
  activeFilter: string = 'All';
  searchQuery: string = '';

  categories = [
    'All',
    'Installation/Uninstallation',
    'Teams & Roles',
    'Monitoring',
    'Reports',
    'Billing & Integration'
  ];

  items = [
    {
      id: 19,
      category: 'Installation/Uninstallation',
      iconName: 'install_desktop',
      title: "How to install SuperSee on Windows 10 & 11 (Unzip to installation completion)",
      description: `<p>Follow the step-by-step instructions below to install SuperSee Time Tracker on Windows 10 &amp; 11 devices:</p>
        <ol>
          <li><b>Download the Installer Package:</b> Navigate to the <b>Employees</b> list or Employee Profile, open the <b>Download</b> menu, and select <b>Windows 10 &amp; 11</b> for your chosen tracking mode (<i>Employee Controlled</i>, <i>Hidden</i>, or <i>Admin Controlled</i>).</li>
          <li><b>Locate the Downloaded File:</b> Open your system's <b>Downloads</b> folder and locate the downloaded SuperSee <code>.zip</code> archive.</li>
          <li><b>Extract / Unzip the Files:</b> Right-click the <code>.zip</code> file, select <b>Extract All...</b>, choose a destination folder, and click <b>Extract</b>. Ensure all files are extracted into a normal folder before running setup.</li>
          <li><b>Run the Setup File:</b> Open the extracted folder and double-click the <code>.msi</code> (for Admin Controlled / Hidden, keep the secrets JSON next to the MSI). When Windows asks for permission, click <b>Yes</b>.</li>
          <li><b>Windows Defender SmartScreen:</b> If Windows displays a <i>"Windows protected your PC"</i> popup, click <b>More info</b> and then click <b>Run anyway</b>. If User Account Control (UAC) asks for permission, click <b>Yes</b>.</li>
          <li><b>Complete Installation:</b> The signed installer package will launch and automatically complete all installation steps. Once finished, it will close automatically.</li>
          <li><b>Sign in or Silent Activation:</b>
            <ul style="list-style-type: disc; padding-left: 20px; margin-top: 4px;">
              <li><b>Employee Controlled / Admin Controlled:</b> Launch SuperSee from the Start Menu and sign in using the provided credentials.</li>
              <li><b>Hidden Mode:</b> SuperSee automatically starts running silently in the background (as <code>Settings</code>) — no employee sign-in or window is required. You can safely delete the installer folder.</li>
            </ul>
          </li>
          <li><b>Restart the System:</b> Restart the computer (mandatory) to ensure all background tracking services initialize properly.</li>
        </ol>`
    },
    {
      id: 1,
      category: 'Installation/Uninstallation',
      iconName: 'install_desktop',
      title: "I’ve created employees, but why is there no data?",
      description: `<p>Make sure that employees or your management team have the <b>SuperSee Time Tracker App</b> installed on their system. Without the desktop app, no data will be recorded. If the setup is still pending, follow these steps:</p>
        <ol>
            <li>Go to the <b>Employees</b> page.</li>
            <li>Click the <b>Actions</b> button and select the <b>Download</b> icon.</li>
            <li>Choose the <b>operating system</b> and the preferred mode (<b>Hidden</b>, <b>Employee Controlled</b>, or <b>Admin Controlled</b>).</li>
            <li>Download and install the app on the respective employee’s system.</li>
        </ol>`
    },
    {
      id: 2,
      category: 'Teams & Roles',
      iconName: 'badge',
      title: "How would my employees receive their credentials?",
      description: `When creating or importing employees, if you selected the <b>Send E- mail to Employees ? </b> option, they would have received an email. Otherwise, you’ll need to keep track of their credentials or reset passwords if needed.
<p>If you used the <b>Import</b> feature, a file containing each employee's password will be shared with you after a successful import.</p>`
    },
    {
      id: 3,
      category: 'Installation/Uninstallation',
      iconName: 'article',
      title: "Supersee Time Tracker Installation Instructions for Windows, Linux and Mac",
      description: `<p>Follow the video tutorials below for step-by-step installation instructions on your preferred operating system:</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div class="p-3 bg-gray-50 rounded-lg border border-gray-100 shadow-sm">
            <h4 class="text-[13px] font-bold text-gray-800 my-2">Windows Installation</h4>
            <div class="flex flex-col gap-2">
              <a href="https://youtu.be/0iZHCjr2sqw" target="_blank" class="text-[12px] text-[#5459d1] hover:underline flex items-center gap-1 font-medium px-2 py-2">
                <span class="material-icons-outlined text-[14px]">play_circle</span> Employee Controlled
              </a>
              <a href="https://youtu.be/2UG148csaWI" target="_blank" class="text-[12px] text-[#5459d1] hover:underline flex items-center gap-1 font-medium px-2 py-2">
                <span class="material-icons-outlined text-[14px]">play_circle</span> Hidden Version
              </a>
            </div>
          </div>
          <div class="p-3 bg-gray-50 rounded-lg border border-gray-100 shadow-sm">
            <h4 class="text-[13px] font-bold text-gray-800 my-2">MacOS Installation</h4>
            <div class="flex flex-col gap-2">
              <a href="https://youtu.be/EpatnT22jIQ" target="_blank" class="text-[12px] text-[#5459d1] hover:underline flex items-center gap-1 font-medium px-2 py-2">
                <span class="material-icons-outlined text-[14px]">play_circle</span> Employee Controlled
              </a>
              <a href="https://youtu.be/xwUbtw1pdkk" target="_blank" class="text-[12px] text-[#5459d1] hover:underline flex items-center gap-1 font-medium px-2 py-2">
                <span class="material-icons-outlined text-[14px]">play_circle</span> Hidden Version
              </a>
            </div>
          </div>
          <div class="p-3 bg-gray-50 rounded-lg border border-gray-100 shadow-sm">
            <h4 class="text-[13px] font-bold text-gray-800 my-2">Linux Installation</h4>
            <div class="flex flex-col gap-2">
              <a href="https://youtu.be/sX0ZplOGiH0" target="_blank" class="text-[12px] text-[#5459d1] hover:underline flex items-center gap-1 font-medium px-2 py-2">
                <span class="material-icons-outlined text-[14px]">play_circle</span> Employee Controlled
              </a>
              <a href="https://youtu.be/vvFTPPbesto" target="_blank" class="text-[12px] text-[#5459d1] hover:underline flex items-center gap-1 font-medium px-2 py-2">
                <span class="material-icons-outlined text-[14px]">play_circle</span> Hidden Version
              </a>
            </div>
          </div>
        </div>
        <p class="mt-4"><a target="_blank" href="https://supersee.io/contact-us/">Contact Us</a> or call us on <a href="tel:+91 9016310001">9016310001</a> for more details.</p>`
    },
    {
      id: 4,
      category: 'Monitoring',
      iconName: 'timeline',
      title: "Overview of timeline of a particular day",
      description: "<b>Productive Hours</b> - Highlighted with purple background</p><p><b>Idle Hours</b> - Highlighted with gray background</p><p><b>Break Hours</b> - Highlighted with white background<br/><p>By, clicking on the specific employee timeline following insights can be obtained</p><ol type='i'><li>First & Last Activity Time</li><li>Total Productive & Idle Hours</li><li>Application Usage</li></ol><center><img width='80%' src='../../assets/employees-timeline.png'></center"
    },
    {
      id: 5,
      category: 'Monitoring',
      iconName: 'shutter_speed',
      title: "What is the Invisible Tracking version, and how can I enable it for my employees?",
      description: "The Invisible Tracking version of the desktop app runs discreetly, ensuring that neither the app nor the widget is visible on the employee's system. To enable invisible tracking, download and install the employee-specific Invisible Tracking Agent file on each employee's device."
    },
    {
      id: 6,
      category: 'Teams & Roles',
      iconName: 'manage_accounts',
      title: "How can I change a specific employee’s settings?",
      description: "Go to the employee's tab and click on the three dots, click on ‘Settings’, turn on personalized settings and change settings just for that one employee."
    },
    {
      id: 7,
      category: 'Billing & Integration',
      iconName: 'account_balance_wallet',
      title: "How can I buy/extend my plan?",
      description: "Click <a href='https://app.supersee.io/billings'>here</a>, to buy/extend your plan as per your requirements!"
    },
    {
      id: 8,
      category: 'Teams & Roles',
      iconName: 'assignment_ind',
      title: "Different type of employee roles and its specification",
      description: "<p>Employee:</p><ol><li>Can view their own logs from Supersee Monitoring Desktop Application</li></ol><p>Team Lead:</p><ol><li>Can view logs of their team members</li><li>Can request screenshot/screencast</li></ol><p>Manager:</p><ol><li>Can view logs of their team members</li><li>Can request screenshot/screencast</li><li>Can change employee specific settings</li></ol>"
    },
    {
      id: 9,
      category: 'Teams & Roles',
      iconName: 'groups',
      title: "How can I create teams?",
      description: "<ol><li>Click on Teams menu item</li><li>Click on Add team and add team name</li><li>Assign manager and team lead</li><li>Add employees to the team</li></ol>"
    },
    {
      id: 10,
      category: 'Billing & Integration',
      iconName: 'hub',
      title: "How can I integrate Supersee with Discord?",
      description: "<ol><li>Follow the steps mentioned <a href='https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks'>here</a> to create two Discord Webhooks for Screenshot & Summary updates.</li><li>In SuperSee admin panel, navigate to Settings->Discord Hooks.</li><li>Copy and paste webhook url(s) appropriately and the integration is done.</li><li>You would start receiving updates on respective Discord Channels.</li></ol>"
    },
    {
      id: 11,
      category: 'Monitoring',
      iconName: 'screenshot',
      title: "How to Request screenshot and Screencast of particular employee",
      description: "<p>There are 2 ways where you can request a screenshot/screencast of an employee</p><ol><li>Navigate to the employee list, click on the screencast/screenshot icon for a specific employee.</li><li>Under employee profile, click on Screenshot Request / Screencast Button.</li></ol>"
    },
    {
      id: 12,
      category: 'Reports',
      iconName: 'summarize',
      title: "How to see hours worked of all employees of a particular day",
      description: "<ol><li>Click on Timesheets menu item</li><li>By default Day wise toggle would be applicable</li><li>Select date</li><li>Click on export button</li></ol>"
    },
    {
      id: 13,
      category: 'Reports',
      iconName: 'calendar_month',
      title: "How to see hours worked of particular Employee with custom date range",
      description: "<ol><li>Click on Timesheets menu item</li><li>Enable toggle switch to filter user wise</li><li>Select user and date range</li><li>Click on export button</li></ol>"
    },
    {
      id: 14,
      category: 'Installation/Uninstallation',
      iconName: 'delete_sweep',
      title: "How to Uninstall Invisible Tracking Version from Employees Device?",
      description: `To remove the SuperSee Time Tracker Invisible Tracking Version, use the appropriate uninstaller for the operating system with administrator privileges:
            <p>
              <ul>
              <li><b>Windows:</b> Run the <a href="./../../assets/Windows/Remove/Remove_SuperSee.bat">Uninstaller</a> file.</li>
                <li><b>Linux:</b> Execute the <a href="./../../assets/Linux/Remove_SuperSee.sh">Uninstaller</a> script.</li>
                <li><b>Mac:</b> Open and run the <a href="./../../assets/Mac/Remove_SuperSee.command">Uninstaller</a> package.</li>
              </ul>
            </p>`
    },
    {
      id: 15,
      category: 'Monitoring',
      iconName: 'sync_disabled',
      title: "Offline Tracking with Auto Sync (Track Employee Time Without Internet)",
      description: "SuperSee works seamlessly even when the internet is down. The desktop app continues to record activity, screenshots, and usage data offline. Once the connection is restored, the data is automatically synced to the server without any manual intervention.",
      videoUrl: "https://www.youtube.com/watch?v=BEt1h5MKNRc&feature=youtu.be"
    },
    {
      id: 16,
      category: 'Reports',
      iconName: 'visibility_off',
      title: "The Blind Spot (What Leaders Don’t See)",
      description: "Understand the gaps in project visibility and how SuperSee helps leaders identify bottlenecks that are often ignored. This feature provides deep insights into project timelines and actual work progress.",
      videoUrl: "https://www.youtube.com/shorts/Isv1pxrWPMI?si=XtClfXRVPJWVvDuZ"
    },
    {
      id: 17,
      category: 'Monitoring',
      iconName: 'psychology',
      title: "Ethical Productivity: Why More Hours DON'T Mean More Output",
      description: "Focus on meaningful work rather than just clocking hours. Learn how SuperSee promotes ethical productivity by tracking quality output and engagement instead of just screen time.",
      videoUrl: "https://www.youtube.com/watch?v=Rc0MxDhci9U&feature=youtu.be"
    },
    {
      id: 18,
      category: 'Installation/Uninstallation',
      iconName: 'rocket_launch',
      title: "How to Add & Onboard Employees in Seconds",
      description: "Get your team up and running instantly. This tutorial shows you how to create employee profiles, invite them to the platform, and start tracking in minutes.",
      videoUrl: "https://www.youtube.com/watch?v=hRkm745LEHs&feature=youtu.be"
    }
  ];

  getSafeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  private stripHtml(html: string): string {
    return html ? html.replace(/<[^>]*>/g, ' ') : '';
  }

  private getStems(word: string): string[] {
    const stems = [word];
    if (word.endsWith('ing') && word.length > 5) stems.push(word.slice(0, -3));
    if (word.endsWith('ation') && word.length > 6) stems.push(word.slice(0, -5));
    if (word.endsWith('ations') && word.length > 7) stems.push(word.slice(0, -6));
    if (word.endsWith('ed') && word.length > 4) stems.push(word.slice(0, -2));
    if (word.endsWith('er') && word.length > 4) stems.push(word.slice(0, -2));
    if (word.endsWith('ers') && word.length > 5) stems.push(word.slice(0, -3));
    if (word.endsWith('s') && !word.endsWith('ss') && word.length > 3) stems.push(word.slice(0, -1));
    return stems;
  }

  get filteredItems() {
    const rawQuery = (this.searchQuery || '').trim();
    if (!rawQuery) {
      return this.items.filter(item => this.activeFilter === 'All' || item.category === this.activeFilter);
    }

    const query = rawQuery.toLowerCase();
    const rawTokens = query.split(/[\s,?.!/\\-]+/).filter(t => t.length > 0);

    const stopWords = new Set([
      'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
      'in', 'on', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'to', 'from', 'up', 'down', 'out', 'over', 'under',
      'i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'you', 'your', 'yours', 'he', 'him', 'his', 'she', 'her', 'hers', 'it', 'its', 'they', 'them', 'their',
      'what', 'which', 'who', 'whom', 'this', 'that', 'these', 'those', 'am', 'have', 'has', 'had', 'do', 'does', 'did', 'doing', 'can', 'could', 'should', 'would', 'will', 'shall', 'may', 'might', 'must',
      'how'
    ]);

    const keywords = rawTokens.filter(t => !stopWords.has(t) && t.length > 1);
    const searchTokens = keywords.length > 0 ? keywords : rawTokens;

    // Filter items matching active category
    const categoryMatchedItems = this.items.filter(item =>
      this.activeFilter === 'All' || item.category === this.activeFilter
    );

    const scoredItems: { item: typeof this.items[0]; score: number }[] = [];

    for (const item of categoryMatchedItems) {
      const titleLower = (item.title || '').toLowerCase();
      const catLower = (item.category || '').toLowerCase();
      const descLower = this.stripHtml(item.description || '').toLowerCase();

      let score = 0;

      // 1. Direct exact phrase match (highest priority)
      if (titleLower.includes(query)) {
        score += 120;
      } else if (catLower.includes(query)) {
        score += 90;
      } else if (descLower.includes(query)) {
        score += 70;
      }

      // 2. Token & keyword matching with stemming
      let matchedTokensCount = 0;
      for (const token of searchTokens) {
        const stems = this.getStems(token);
        const matchesStem = (text: string) => stems.some(stem => text.includes(stem));

        const inTitle = matchesStem(titleLower);
        const inCat = matchesStem(catLower);
        const inDesc = matchesStem(descLower);

        if (inTitle || inCat || inDesc) {
          matchedTokensCount++;
          if (inTitle) score += 30;
          if (inCat) score += 20;
          if (inDesc) score += 15;
        }
      }

      // Bonus if all search tokens matched
      if (matchedTokensCount === searchTokens.length && searchTokens.length > 0) {
        score += 50;
      }

      if (score > 0) {
        scoredItems.push({ item, score });
      }
    }

    // Sort by relevance score descending
    return scoredItems.sort((a, b) => b.score - a.score).map(entry => entry.item);
  }

  setActiveFilter(filter: string) {
    this.activeFilter = filter;
  }

  expandedIndex = 0;
}
